import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import "../styles/premium-grid.css";

const PrivateVoidGrid = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [paymentId, setPaymentId] = useState('');
  const [loginStatus, setLoginStatus] = useState('');
  const [statusColor, setStatusColor] = useState('#666');
  const [userTokens, setUserTokens] = useState(0);
  const [selectedCell, setSelectedCell] = useState(null);
  const [messageType, setMessageType] = useState('text');
  const [messageText, setMessageText] = useState('');
  const [expirationTime, setExpirationTime] = useState(10);
  const [audioChunks, setAudioChunks] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState('0:00');
  const [recordingStatus, setRecordingStatus] = useState('');
  const [messages, setMessages] = useState([]);
  const [activePopup, setActivePopup] = useState(null);
  const [autoDecodingIds, setAutoDecodingIds] = useState(new Set());
  const [encodingKeyNotification, setEncodingKeyNotification] = useState(null);
  const [notificationTimeout, setNotificationTimeout] = useState(null);
  const [coordinates, setCoordinates] = useState({ x: '', y: '' });
  const [isPremiumUser, setIsPremiumUser] = useState(true);//false
  
  // Mobile-specific states
  const [isMobileView, setIsMobileView] = useState(false);
  const [activeTab, setActiveTab] = useState('grid');
  const [showMobileDragHint, setShowMobileDragHint] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);

  const gridRef = useRef(null);
  const audioRecorderRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const recordingStartTimeRef = useRef(0);
  const audioPreviewRef = useRef(null);
  
  // Mobile touch refs
  const touchStartRef = useRef({ x: 0, y: 0 });
  const lastTouchRef = useRef({ x: 0, y: 0 });
  const isTouchDraggingRef = useRef(false);
  const touchTimeRef = useRef(0);

  const apiUrl = 'https://api.omegavoid.com';
  const MAX_RECORDING_TIME = 5 * 60 * 1000;

  const GRID_WIDTH = 20;
  const GRID_HEIGHT = 40;

  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    
    const hasVisitedBefore = localStorage.getItem('hasVisitedPrivateVoid');
    if (!hasVisitedBefore && isMobileView) {
      setShowMobileDragHint(true);
      setTimeout(() => setShowMobileDragHint(false), 3000);
      localStorage.setItem('hasVisitedPrivateVoid', 'true');
    }
    
    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      try {
        const tokenPayload = JSON.parse(atob(storedToken.split('.')[1]));
        const username = tokenPayload['cognito:username'] || tokenPayload.username || 'User';
        setUsername(username);
        setIsLoggedIn(true);
        
        const isPremium = localStorage.getItem('isPremiumUser') === "true";
        
        setIsPremiumUser(true);
        if (isPremium) {
          fetchMessages(storedToken);
        }
        
        const storedTokens = localStorage.getItem('userTokens');
        if (storedTokens) {
          setUserTokens(parseFloat(storedTokens));
        }
      } catch (e) {
        console.error('Error parsing stored token:', e);
        localStorage.removeItem('authToken');
      }
    }
    
    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, []);

  const renderGridLines = () => {
    // Return empty array to remove grid lines
    return [];
  };

  const renderCoordinateMarkings = () => {
    const markings = [];
    
    for (let x = -10; x <= 10; x++) {
      if (x % 2 === 0 || x === -10 || x === 10) {
        const xPercent = ((x + 10) / 20) * 100;
        markings.push(
          <div 
            key={`x-${x}`}
            className="grid-coordinate-marking x-marking"
            style={{
              left: `${xPercent}%`
            }}
          >
            {x}
          </div>
        );
      }
    }
    
    for (let y = -20; y <= 20; y++) {
      if (y % 4 === 0 || y === -20 || y === 20) {
        const yPercent = ((20 - y) / 40) * 100;
        markings.push(
          <div 
            key={`y-${y}`}
            className="grid-coordinate-marking y-marking"
            style={{
              top: `${yPercent}%`
            }}
          >
            {y}
          </div>
        );
      }
    }
    
    return markings;
  };

  const renderMessageMarkers = () => {
    return messages.map(message => {
      const pos = getGridPosition(parseFloat(message.y_coordinate), parseFloat(message.x_coordinate));
      
      const xPercent = ((pos.x + 10) / GRID_WIDTH) * 100;
      const yPercent = ((20 - pos.y) / GRID_HEIGHT) * 100;
      
      return (
        <div
          key={message.id}
          className={`message-dot ${message.content_type}`}
          style={{
            left: `${xPercent}%`,
            top: `${yPercent}%`,
            pointerEvents: 'none'
          }}
        >
        </div>
      );
    });
  };

  const renderPersonalVoidFlag = () => {
    return (
      <div className="personal-void-flag">
        Personal Void Mode
      </div>
    );
  };

  const renderPremiumLock = () => {
    return (
      <div className="premium-lock-overlay">
        <div className="premium-lock-content">
          <h2>Premium Feature</h2>
          <p>This private grid space is available exclusively for premium users.</p>
          <p className="price-info">Unlock for 37,500 tokens</p>
          <Link to="/login#" className="premium-purchase-btn">Purchase Now</Link>
        </div>
      </div>
    );
  };
  
  // Mobile UI elements
  const renderMobileTabs = () => {
    if (!isMobileView) return null;
    
    return (
      <div className="mobile-tabs">
        <div 
          className={`mobile-tab ${activeTab === 'grid' ? 'active' : ''}`}
          onClick={() => switchTab('grid')}
        >
          Grid
        </div>
        <div
          className={`mobile-tab ${activeTab === 'controls' ? 'active' : ''}`}
          onClick={() => switchTab('controls')}
        >
          Controls
        </div>
      </div>
    );
  };

  const renderMobileActionBar = () => {
    if (!isMobileView) return null;
    
    return (
      <div className="mobile-action-bar">
        <button onClick={() => switchTab('grid')}>
          <i className="fa fa-th"></i>
          Grid
        </button>
        {/* {isPremiumUser && (
          <button onClick={generateRandomCoordinates}>
            <i className="fa fa-random"></i>
            Random
          </button>
        )} */}
        <button onClick={() => {
          switchTab('controls');
          setTimeout(() => {
            const messageField = document.getElementById('private-void-message-text');
            if (messageField) messageField.focus();
          }, 300);
        }}>
          <i className="fa fa-pen"></i>
          Controls
        </button>
        <button onClick={() => {
          switchTab('controls');
          setTimeout(() => {
            const jumpCoordinatesInput = document.getElementById('jump-coordinates');
            if (jumpCoordinatesInput) jumpCoordinatesInput.focus();
          }, 300);
        }}>
          <i className="fa fa-search"></i>
          Jump
        </button>
      </div>
    );
  };

  const renderMobileDragHint = () => {
    if (!isMobileView || !showMobileDragHint) return null;
    
    return (
      <div className="map-drag-hint">
        Explore the Private Void Grid
      </div>
    );
  };
  
  const switchTab = (tab) => {
    setActiveTab(tab);
    
    if (isMobileView) {
      setTimeout(() => {
        const element = document.querySelector(`.mobile-tab-content.${tab}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);
    }
  };

  const handleGridClick = (e) => {
    if (!gridRef.current || !isPremiumUser) return;
    
    const rect = gridRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    
    const xPercent = clickX / rect.width;
    const yPercent = clickY / rect.height;
    
    const gridX = Math.round((xPercent * GRID_WIDTH) - 10);
    const gridY = Math.round(20 - (yPercent * GRID_HEIGHT));
    
    setSelectedCell({ x: gridX, y: gridY });
    
    const latitude = gridY ;
    const longitude = gridX ;
    
    setCoordinates({
      x: longitude.toFixed(2),
      y: latitude.toFixed(2)
    });
  };
  
  const handleTouchStart = (e) => {
    if (!isPremiumUser) return;
    
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    lastTouchRef.current = { x: touch.clientX, y: touch.clientY };
    touchTimeRef.current = Date.now();
    isTouchDraggingRef.current = false;
  };
  
  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    const totalDx = touch.clientX - touchStartRef.current.x;
    const totalDy = touch.clientY - touchStartRef.current.y;
    
    if (Math.abs(totalDx) > 10 || Math.abs(totalDy) > 10) {
      isTouchDraggingRef.current = true;
      
      if (!hasDragged) {
        setHasDragged(true);
      }
    }
    
    lastTouchRef.current = { x: touch.clientX, y: touch.clientY };
  };
  
  const handleTouchEnd = (e) => {
    const touchDuration = Date.now() - touchTimeRef.current;
    
    if (!isTouchDraggingRef.current && touchDuration < 300 && isPremiumUser) {
      const touch = e.changedTouches[0];
      
      const rect = gridRef.current.getBoundingClientRect();
      const touchX = touch.clientX - rect.left;
      const touchY = touch.clientY - rect.top;
      
      const xPercent = touchX / rect.width;
      const yPercent = touchY / rect.height;
      
      const gridX = Math.round((xPercent * GRID_WIDTH) - 10);
      const gridY = Math.round(20 - (yPercent * GRID_HEIGHT));
      
      setSelectedCell({ x: gridX, y: gridY });
      
      const latitude = gridY ;
      const longitude = gridX ;
      
      setCoordinates({
        x: longitude.toFixed(2),
        y: latitude.toFixed(2)
      });
      
      if (isMobileView) {
        switchTab('controls');
      }
    }
    
    isTouchDraggingRef.current = false;
  };

  const generateRandomCoordinates = () => {
    const randX = Math.floor(Math.random() * (GRID_WIDTH + 1)) - 10;
    const randY = Math.floor(Math.random() * (GRID_HEIGHT + 1)) - 20;
    
    const longitude = (randX ).toFixed(2);
    const latitude = (randY ).toFixed(2);
    
    setCoordinates({
      x: longitude,
      y: latitude
    });
    
    setSelectedCell({ x: randX, y: randY });
  };

  const handleLogin = async () => {
    if (!paymentId.trim()) {
      setLoginStatus('Please enter your Login ID');
      setStatusColor('#d32f2f');
      return;
    }
    
    setLoginStatus('Logging in...');
    setStatusColor('#000');
    
    try {
      const response = await fetch(`${apiUrl}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          payment_id: paymentId
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.id_token) {
        localStorage.setItem('authToken', data.id_token);
        setLoginStatus('Login successful!');
        setStatusColor('#4CAF50');
        
        setIsLoggedIn(true);
        
        try {
          const tokenPayload = JSON.parse(atob(data.id_token.split('.')[1]));
          const username = tokenPayload['cognito:username'] || tokenPayload.username || 'User';
          setUsername(username);
        } catch (e) {
          setUsername('Authenticated User');
        }
        
        if (data.tokens) {
          localStorage.setItem('userTokens', data.tokens.toString());
          setUserTokens(data.tokens);
        }
        
        if (data.temp_user_id) {
          localStorage.setItem('tempUserId', data.temp_user_id);
        }
        
        if (data.isPremium === true) {
          setIsPremiumUser(true);
          localStorage.setItem('isPremiumUser', true);
          const authToken = localStorage.getItem('authToken');
          fetchMessages(authToken);
        } else {
          setIsPremiumUser(true); //false
          localStorage.setItem('isPremiumUser', false);
        }
      } else {
        setLoginStatus(data.message || 'Login failed. Please check your Login ID.');
        setStatusColor('#d32f2f');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginStatus('Connection error. Please try again.');
      setStatusColor('#d32f2f');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userTokens');
    localStorage.removeItem('tempUserId');
    localStorage.removeItem('isPremiumUser');
    setIsLoggedIn(false);
    setUsername('');
    setPaymentId('');
    setUserTokens(0);
    setIsPremiumUser(true); //false
    setLoginStatus('Logged out successfully');
    setStatusColor('#4CAF50');
  };

  const fetchMessages = async (authToken) => {
    try {
      const response = await fetch(`${apiUrl}/personal-messages`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        const filteredMessages = (data.messages || []).filter(message => {
          const x = parseFloat(message.x_coordinate);
          const y = parseFloat(message.y_coordinate);
          return x >= -10 && x <= 10 && y >= -20 && y <= 20;
        });
        
        setMessages(filteredMessages);
      } else {
        console.error('Error fetching messages:', data.error);
        if (response.status === 401) {
          handleLogout();
        }
      }
    } catch (error) {
      console.error('Error connecting to server:', error);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioRecorderRef.current = recorder;
      
      const chunks = [];
      recorder.addEventListener('dataavailable', event => {
        chunks.push(event.data);
      });
      
      recorder.addEventListener('stop', () => {
        setRecordingStatus('Recording saved!');
        setIsRecording(false);
        
        if (recordingIntervalRef.current) {
          clearInterval(recordingIntervalRef.current);
          recordingIntervalRef.current = null;
        }
        
        setAudioChunks(chunks);
        
        const audioBlob = new Blob(chunks, { type: 'audio/mp3' });
        const audioUrl = URL.createObjectURL(audioBlob);
        audioPreviewRef.current = audioUrl;
      });
      
      recorder.start();
      setIsRecording(true);
      recordingStartTimeRef.current = Date.now();
      
      setRecordingStatus('Recording...');
      setRecordingTime('0:00');
      
      recordingIntervalRef.current = setInterval(() => {
        const elapsedMs = Date.now() - recordingStartTimeRef.current;
        const minutes = Math.floor(elapsedMs / 60000);
        const seconds = Math.floor((elapsedMs % 60000) / 1000);
        setRecordingTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        
        if (elapsedMs >= MAX_RECORDING_TIME) {
          if (audioRecorderRef.current && isRecording) {
            audioRecorderRef.current.stop();
            setRecordingStatus('Maximum recording time reached (5 minutes)');
          }
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      setRecordingStatus('Error: Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (audioRecorderRef.current && isRecording) {
      audioRecorderRef.current.stop();
    }
  };
  const copyToClipboard = async (text, buttonElement = null) => {
    // Get the button element if not passed as parameter
    const button = buttonElement || document.activeElement;
    
    try {
      // For modern browsers with clipboard API
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        showCopySuccess(button);
        return;
      }
      
      // Fallback for older browsers or non-secure contexts
      throw new Error('Clipboard API not available');
      
    } catch (err) {
      console.log('Using fallback copy method:', err.message);
      
      // Enhanced fallback for Safari and older browsers
      try {
        await safariCompatibleCopy(text);
        showCopySuccess(button);
      } catch (fallbackErr) {
        console.error('All copy methods failed:', fallbackErr);
        
        // Final fallback - show the text for manual copying
        if (window.confirm(`Copy failed. Click OK to see the text to copy manually:\n\n${text}`)) {
          // User clicked OK, they've seen the text
        }
      }
    }
  };
  
  const safariCompatibleCopy = (text) => {
    return new Promise((resolve, reject) => {
      // Create a text area element
      const textArea = document.createElement('textarea');
      
      // Configure the text area to be invisible and non-disruptive
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '-9999px';
      textArea.style.opacity = '0';
      textArea.style.pointerEvents = 'none';
      textArea.style.zIndex = '-1';
      textArea.setAttribute('readonly', '');
      textArea.setAttribute('aria-hidden', 'true');
      
      try {
        // Add to DOM
        document.body.appendChild(textArea);
        
        // Focus and select (required for Safari)
        textArea.focus();
        textArea.select();
        textArea.setSelectionRange(0, textArea.value.length);
        
        // For iOS Safari, we need to handle this differently
        if (navigator.userAgent.match(/ipad|iphone/i)) {
          const range = document.createRange();
          range.selectNodeContents(textArea);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
          textArea.setSelectionRange(0, 999999);
        }
        
        // Execute copy command
        const successful = document.execCommand('copy');
        
        if (successful) {
          resolve();
        } else {
          reject(new Error('execCommand copy failed'));
        }
        
      } catch (err) {
        reject(err);
      } finally {
        // Clean up - remove the text area
        try {
          if (textArea.parentNode) {
            document.body.removeChild(textArea);
          }
        } catch (cleanupErr) {
          console.warn('Cleanup failed:', cleanupErr);
        }
      }
    });
  };
  const showCopySuccess = (button) => {
    // Handle button feedback
    if (button && button.tagName === 'BUTTON') {
      const originalText = button.textContent;
      const originalClass = button.className;
      
      button.textContent = "Copied!";
      button.classList.add('copied');
      button.disabled = true;
      
      setTimeout(() => {
        try {
          button.textContent = originalText;
          button.className = originalClass;
          button.disabled = false;
        } catch (err) {
          console.warn('Button reset failed:', err);
        }
      }, 2000);
    }
    
    // Show mobile toast if needed
    if (isMobileView) {
      showToast('Copied to clipboard!');
    }
  };    

  
  const showToast = (message) => {
    // Remove any existing toasts first
    const existingToasts = document.querySelectorAll('.copy-toast');
    existingToasts.forEach(toast => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    });
    
    const toast = document.createElement('div');
    toast.className = 'copy-toast';
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 12px 24px;
      border-radius: 6px;
      z-index: 10000;
      font-size: 14px;
      opacity: 0;
      transition: opacity 0.3s ease;
      pointer-events: none;
    `;
    
    document.body.appendChild(toast);
    
    // Trigger animation
    requestAnimationFrame(() => {
      toast.style.opacity = '1';
    });
    
    // Remove toast
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => {
        if (toast.parentNode) {
          document.body.removeChild(toast);
        }
      }, 300);
    }, 2000);
  };
  
  const copyToClipboardz = (text) => {
    try {
      navigator.clipboard.writeText(text)
        .then(() => {
          try {
            const button = document.activeElement;
            if (button) {
              const originalText = button.textContent;
              button.textContent = "Copied!";
              button.classList.add('copied');
              
              setTimeout(() => {
                button.textContent = originalText;
                button.classList.remove('copied');
              }, 2000);
            }
            
            if (isMobileView) {
              const toast = document.createElement('div');
              toast.className = 'copy-toast';
              toast.textContent = 'Copied to clipboard!';
              document.body.appendChild(toast);
              
              setTimeout(() => {
                toast.classList.add('show');
              }, 10);
              
              setTimeout(() => {
                toast.classList.remove('show');
                setTimeout(() => {
                  if (toast.parentNode) {
                    document.body.removeChild(toast);
                  }
                }, 300);
              }, 2000);
            }
          } catch (innerError) {
            console.error('Error updating UI after copy:', innerError);
          }
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
          alert('Failed to copy to clipboard');
        });
    } catch (err) {
      console.error('Copy to clipboard error:', err);
      alert('Failed to copy to clipboard');
    }
  };

  useEffect(() => {
    let timerInterval;
    
    if (encodingKeyNotification) {
      timerInterval = setInterval(() => {
        const secondsLeft = Math.ceil(60 - (Date.now() - encodingKeyNotification.timestamp) / 1000);
        
        const timerElement = document.querySelector('.notification-timer');
        if (timerElement) {
          timerElement.textContent = `This notification will close in ${secondsLeft > 0 ? secondsLeft : 0} seconds`;
        }
        
        if (secondsLeft <= 0) {
          clearInterval(timerInterval);
        }
      }, 1000);
    }
    
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [encodingKeyNotification]);

  useEffect(() => {
    return () => {
      if (notificationTimeout) {
        clearTimeout(notificationTimeout);
      }
    };
  }, [notificationTimeout]);

  useEffect(() => {
    let popupTimer;
    
    if (activePopup) {
      popupTimer = setTimeout(() => {
        closePopup();
      }, 60000);
    }
    
    return () => {
      if (popupTimer) {
        clearTimeout(popupTimer);
      }
    };
  }, [activePopup]);

  const submitMessage = async () => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      alert('Please login to perform this action');
      return;
    }
    
    if (!isPremiumUser) {
      alert('You need to be a premium user to post messages in the private void');
      return;
    }
    
    if (!selectedCell) {
      alert('Please select a cell on the grid');
      return;
    }
    
    const xCoordinate = parseFloat(coordinates.x);
    const yCoordinate = parseFloat(coordinates.y);
    
    if (isNaN(xCoordinate) || isNaN(yCoordinate)) {
      alert('Please provide valid coordinates');
      return;
    }
    
    if (isNaN(expirationTime) || expirationTime < 2 || expirationTime > 60) {
      alert('Expiration time must be between 2 and 60 minutes');
      return;
    }
    
    const requestData = {
      content_type: messageType,
      expiration_time: expirationTime,
      x_coordinate: xCoordinate ,
      y_coordinate: yCoordinate
    };
    
    if (messageType === 'text') {
      if (!messageText.trim()) {
        alert('Please enter a message');
        return;
      }
      
      requestData.text = messageText;
    } else {
      if (!audioChunks.length) {
        alert('Please record an audio message');
        return;
      }
      
      const audioBlob = new Blob(audioChunks, { type: 'audio/mp3' });
      const reader = new FileReader();
      
      reader.readAsDataURL(audioBlob);
      reader.onloadend = function() {
        const base64Audio = reader.result.split(',')[1];
        requestData.audio = base64Audio;
        sendMessageToApi(requestData, authToken);
      };
      return;
    }
    
    sendMessageToApi(requestData, authToken);
    
    if (isMobileView) {
      setTimeout(() => {
        switchTab('grid');
      }, 500);
    }
  };

  const sendMessageToApi = async (requestData, authToken) => {
    try {
      const response = await fetch(`${apiUrl}/personal-messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken
        },
        body: JSON.stringify(requestData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.tokens !== undefined) {
          setUserTokens(data.tokens);
          localStorage.setItem('userTokens', data.tokens.toString());
        }
        
        const encodingKey = data.encryption_key || 'Not available';
        
        if (notificationTimeout) {
          clearTimeout(notificationTimeout);
        }
        
        setEncodingKeyNotification({
          key: encodingKey,
          coordinates: requestData.x_coordinate +','+ requestData.y_coordinate,
          message: 'Your message has been cast into the void! You have 60 seconds to copy your coordinates and access code. After this information will not be available anymore',
          timestamp: Date.now()
        });
        
        const timeout = setTimeout(() => {
          setEncodingKeyNotification(null);
        }, 60000);
        
        setNotificationTimeout(timeout);
        
        setMessageText('');
        setCoordinates({ x: '', y: '' });
        setAudioChunks([]);
        audioPreviewRef.current = null;
        setSelectedCell(null);
        
        fetchMessages(authToken);
      } else {
        if (response.status === 403 && data.error === 'Insufficient tokens') {
          alert(`${data.message || 'You do not have enough tokens for this action.'}`);
        } else {
          alert(`Error: ${data.error || 'Failed to post message'}`);
        }
      }
    } catch (error) {
      console.error('Error posting message:', error);
      alert('Failed to connect to the server. Please try again later.');
    }
  };

// Add these helper functions to your component

// Helper function to detect if popup would be clipped
const jumpToCoordinate = async () => {
  let jumpX, jumpY, decodingKey = null;
  
  if (document.getElementById('jump-coordinates')) {
    const input = document.getElementById('jump-coordinates').value.trim();
    
    // Parse Omega Link format: omega://x=X&y=Y&key=KEY
    const omegaLinkMatch = input.match(/omega:\/\/x=([-+]?\d+(?:\.\d+)?)&y=([-+]?\d+(?:\.\d+)?)(?:&key=([^&\s]+))?/i);
    
    if (omegaLinkMatch) {
      jumpX = parseFloat(omegaLinkMatch[1]);
      jumpY = parseFloat(omegaLinkMatch[2]);
      decodingKey = omegaLinkMatch[3] || null;
    } else {
      // Fallback to legacy format: X,Y or X,Y,KEY
      let parts = input.split(',').map(val => val.trim());
      
      if (parts.length < 2) {
        alert('Please enter format: omega://x=X&y=Y&key=K or X,Y,Key');
        return;
      }
      
      jumpX = parseFloat(parts[0]);
      jumpY = parseFloat(parts[1]);
      decodingKey = parts.length >= 3 ? parts.slice(2).join(',').trim() : null;
    }
  } else {
    const jumpXInput = document.getElementById('jump-x-coordinate');
    const jumpYInput = document.getElementById('jump-y-coordinate');
    const keyInput = document.getElementById('jump-key-coordinate');
    
    if (!jumpXInput || !jumpYInput) return;
    
    jumpX = parseFloat(jumpXInput.value);
    jumpY = parseFloat(jumpYInput.value);
    decodingKey = keyInput ? keyInput.value.trim() : null;
  }
  
  // Validate coordinates
  if (isNaN(jumpX) || isNaN(jumpY)) {
    alert('Please enter valid grid coordinates');
    return;
  }
  
  // Personal Void grid bounds: X: -10 to 10, Y: -20 to 20
  if (jumpX < -10 || jumpX > 10 || jumpY < -20 || jumpY > 20) {
    alert('Personal Void coordinates must be within X: -10 to 10, Y: -20 to 20');
    return;
  }
  
  closePopup();
  
  // Calculate grid position
  const xPercent = ((jumpX + 10) / GRID_WIDTH) * 100;
  const yPercent = ((20 - jumpY) / GRID_HEIGHT) * 100;
  
  // Set selected cell
  setSelectedCell({ x: jumpX, y: jumpY });
  setCoordinates({
    x: jumpX.toFixed(2),
    y: jumpY.toFixed(2)
  });
  
  // Show success message
  
  // Check for existing message at coordinates
  const existingMessage = messages.find(message => 
    Math.abs(parseFloat(message.x_coordinate) - jumpX) < 0.01 && 
    Math.abs(parseFloat(message.y_coordinate) - jumpY) < 0.01
  );
  
  if (existingMessage) {
    // Auto-decode if key provided
    if (decodingKey) {
      setTimeout(() => {
        showMessagePopup(existingMessage, jumpX, jumpY, xPercent, yPercent);
        setTimeout(() => {
          autoDecodeMessage(existingMessage.id, decodingKey);
        }, 300);
      }, 500);
    } else {
      setTimeout(() => {
        showMessagePopup(existingMessage, jumpX, jumpY, xPercent, yPercent);
      }, 500);
    }
  }
  
  if (isMobileView) {
    switchTab('grid');
  }
};

const autoDecodeMessage = async (messageId, encodingKey) => {
  const authToken = localStorage.getItem('authToken');

  if (!authToken) {
    alert('Please login to decode messages');
    return;
  }

  setAutoDecodingIds(prev => new Set(prev).add(messageId));

  try {
    let contentDiv = document.getElementById(`message-content-${messageId}`);
    if (!contentDiv) {
      contentDiv = document.createElement('div');
      contentDiv.id = `message-content-${messageId}`;
      contentDiv.style.marginTop = '10px';
      const popup = document.querySelector('.message-popup');
      if (popup) {
        popup.appendChild(contentDiv);
      }
    }
    
    const response = await fetch(`${apiUrl}/personal-messages/${messageId}?encryption_key=${encodingKey}`, {
      method: 'GET',
      headers: {
        'Authorization': authToken
      }
    });
    
    const data = await response.json();
    
    if (response.ok && data.message) {
      // Hide decode section
      const decodeSection = document.getElementById(`decode-section-${messageId}`);
      if (decodeSection) {
        decodeSection.style.display = 'none';
      }
      
      // Display decoded content
      if (data.message.content_type === 'text' && data.message.decoded_text) {
        contentDiv.innerHTML = `
          <div style="padding: 10px; background-color: #f5f5f5; border-radius: 5px;">
            <strong>Message:</strong>
            <div class="decoded-message-text">${data.message.decoded_text}</div>
          </div>
        `;
      } else if (data.message.content_type === 'audio' && data.message.decoded_audio_base64) {
        const audioSrc = `data:audio/mp3;base64,${data.message.decoded_audio_base64}`;
        contentDiv.innerHTML = `
          <div class="audio-controls">
            <div class="audio-label">Audio Message:</div>
            <audio controls src="${audioSrc}"></audio>
          </div>
        `;
      } else {
        contentDiv.innerHTML = '<div style="color: red;">Could not decode message content</div>';
      }
    } else {
      contentDiv.innerHTML = `<div style="color: red;">Error: ${data.error || 'Invalid access code'}</div>`;
    }
  } catch (error) {
    console.error('Error auto-decoding message:', error);
  } finally {
    setAutoDecodingIds(prev => { const next = new Set(prev); next.delete(messageId); return next; });
  }
};
const detectPopupClipping = (popupElement, dotX, dotY) => {
  const rect = popupElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const navBarHeight = 100; // Adjust based on your nav bar height
  
  return {
    clippedTop: rect.top < navBarHeight,
    clippedBottom: rect.bottom > viewportHeight - 20,
    clippedLeft: rect.left < 10,
    clippedRight: rect.right > viewportWidth - 10,
    dotX,
    dotY
  };
};
const calculateArrowPosition = (popupRect, dotX, dotY, arrowSide) => {
  const arrowSize = 10; // Arrow size in pixels
  let arrowPosition = { left: 'auto', right: 'auto', top: 'auto', bottom: 'auto' };
  
  switch (arrowSide) {
    case 'top': // Arrow points down from top of popup
      const leftOffsetTop = dotX - popupRect.left;
      const leftPercentTop = Math.max(arrowSize, Math.min(popupRect.width - arrowSize, leftOffsetTop));
      arrowPosition.left = `${leftPercentTop}px`;
      arrowPosition.top = `-${arrowSize}px`;
      break;
      
    case 'bottom': // Arrow points up from bottom of popup
      const leftOffsetBottom = dotX - popupRect.left;
      const leftPercentBottom = Math.max(arrowSize, Math.min(popupRect.width - arrowSize, leftOffsetBottom));
      arrowPosition.left = `${leftPercentBottom}px`;
      arrowPosition.bottom = `-${arrowSize}px`;
      break;
      
    case 'left': // Arrow points right from left of popup
      const topOffsetLeft = dotY - popupRect.top;
      const topPercentLeft = Math.max(arrowSize, Math.min(popupRect.height - arrowSize, topOffsetLeft));
      arrowPosition.top = `${topPercentLeft}px`;
      arrowPosition.left = `-${arrowSize}px`;
      break;
      
    case 'right': // Arrow points left from right of popup
      const topOffsetRight = dotY - popupRect.top;
      const topPercentRight = Math.max(arrowSize, Math.min(popupRect.height - arrowSize, topOffsetRight));
      arrowPosition.top = `${topPercentRight}px`;
      arrowPosition.right = `-${arrowSize}px`;
      break;
  }
  
  return arrowPosition;
};

// Function to create and position the arrow dynamically
const createDynamicArrow = (popupElement, dotX, dotY, arrowSide) => {
  // Remove existing arrow
  const existingArrow = popupElement.querySelector('.dynamic-arrow');
  if (existingArrow) {
    existingArrow.remove();
  }
  
  // Create new arrow element
  const arrow = document.createElement('div');
  arrow.className = `dynamic-arrow arrow-${arrowSide}`;
  
  // Calculate arrow position
  const popupRect = popupElement.getBoundingClientRect();
  const arrowPosition = calculateArrowPosition(popupRect, dotX, dotY, arrowSide);
  
  // Apply position styles
  Object.assign(arrow.style, arrowPosition);
  
  // Add the arrow to the popup
  popupElement.appendChild(arrow);
  
  return arrow;
};


const applySmartPositioning = (popupElement, xPercent, yPercent, gridRect) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const navBarHeight = 100;
  const margin = 20;
  
  // Calculate dot position in viewport coordinates
  const dotX = gridRect.left + (xPercent / 100) * gridRect.width;
  const dotY = gridRect.top + (yPercent / 100) * gridRect.height;
  
  // Get popup dimensions (use temporary positioning to measure)
  popupElement.style.visibility = 'hidden';
  popupElement.style.position = 'absolute';
  popupElement.style.left = '0px';
  popupElement.style.top = '0px';
  popupElement.style.transform = 'none';
  
  const popupRect = popupElement.getBoundingClientRect();
  const popupWidth = popupRect.width;
  const popupHeight = popupRect.height;
  
  // Calculate available space in each direction
  const spaceTop = dotY - navBarHeight - margin;
  const spaceBottom = viewportHeight - dotY - margin;
  const spaceLeft = dotX - margin;
  const spaceRight = viewportWidth - dotX - margin;
  
  // Determine best position
  let finalPosition = { x: xPercent, y: yPercent, transformX: '-50%', transformY: '-110%' };
  let arrowSide = 'bottom'; // Default: popup above dot, arrow points down
  
  // Primary decision: vertical positioning
  if (spaceTop >= popupHeight + 20) {
    // Enough space above - position popup above dot
    finalPosition.transformY = `calc(-100% - 15px)`;
    arrowSide = 'bottom';
  } else if (spaceBottom >= popupHeight + 20) {
    // Position popup below dot
    finalPosition.transformY = '15px';
    arrowSide = 'top';
  } else if (spaceLeft >= popupWidth + 20) {
    // Position popup to the left
    finalPosition.transformX = `calc(-100% - 15px)`;
    finalPosition.transformY = '-50%';
    arrowSide = 'right';
  } else if (spaceRight >= popupWidth + 20) {
    // Position popup to the right
    finalPosition.transformX = '15px';
    finalPosition.transformY = '-50%';
    arrowSide = 'left';
  } else {
    // Fallback: best fit with centering adjustments
    if (spaceTop > spaceBottom) {
      finalPosition.transformY = `calc(-100% - 15px)`;
      arrowSide = 'bottom';
    } else {
      finalPosition.transformY = '15px';
      arrowSide = 'top';
    }
  }
  
  // Secondary adjustment: horizontal positioning to avoid edge clipping
  const halfPopupWidth = popupWidth / 2;
  
  if (arrowSide === 'top' || arrowSide === 'bottom') {
    // For vertical positioning, adjust horizontal if needed
    if (dotX - halfPopupWidth < margin) {
      // Too close to left edge
      const gridRelativeLeft = margin + halfPopupWidth;
      finalPosition.x = ((gridRelativeLeft - gridRect.left) / gridRect.width) * 100;
    } else if (dotX + halfPopupWidth > viewportWidth - margin) {
      // Too close to right edge
      const gridRelativeRight = viewportWidth - margin - halfPopupWidth;
      finalPosition.x = ((gridRelativeRight - gridRect.left) / gridRect.width) * 100;
    }
  } else {
    // For horizontal positioning, adjust vertical if needed
    const halfPopupHeight = popupHeight / 2;
    if (dotY - halfPopupHeight < navBarHeight + margin) {
      // Too close to top
      const gridRelativeTop = navBarHeight + margin + halfPopupHeight;
      finalPosition.y = ((gridRelativeTop - gridRect.top) / gridRect.height) * 100;
    } else if (dotY + halfPopupHeight > viewportHeight - margin) {
      // Too close to bottom
      const gridRelativeBottom = viewportHeight - margin - halfPopupHeight;
      finalPosition.y = ((gridRelativeBottom - gridRect.top) / gridRect.height) * 100;
    }
  }
  
  return { ...finalPosition, arrowSide, dotX, dotY };
};
const applyGridAwarePositioning = (popupElement, xPercent, yPercent, gridRect) => {
  const margin = 15; // Margin from grid edges
  
  // Calculate dot position within grid
  const dotX = gridRect.left + (xPercent / 100) * gridRect.width;
  const dotY = gridRect.top + (yPercent / 100) * gridRect.height;
  
  // Get popup dimensions
  popupElement.style.visibility = 'hidden';
  popupElement.style.position = 'absolute';
  popupElement.style.left = '0px';
  popupElement.style.top = '0px';
  popupElement.style.transform = 'none';
  
  const popupRect = popupElement.getBoundingClientRect();
  const popupWidth = popupRect.width;
  const popupHeight = popupRect.height;
  
  // Calculate available space within grid bounds
  const spaceTop = dotY - gridRect.top - margin;
  const spaceBottom = gridRect.bottom - dotY - margin;
  const spaceLeft = dotX - gridRect.left - margin;
  const spaceRight = gridRect.right - dotX - margin;
  
  // Determine best position within grid bounds
  let finalPosition = { x: xPercent, y: yPercent, transformX: '-50%', transformY: '-110%' };
  let arrowSide = 'bottom'; // Default: popup above dot
  
  // Primary decision: vertical positioning within grid
  if (spaceTop >= popupHeight + 20) {
    // Position popup above dot
    finalPosition.transformY = `calc(-100% - 15px)`;
    arrowSide = 'bottom';
  } else if (spaceBottom >= popupHeight + 20) {
    // Position popup below dot
    finalPosition.transformY = '15px';
    arrowSide = 'top';
  } else if (spaceLeft >= popupWidth + 20) {
    // Position popup to the left
    finalPosition.transformX = `calc(-100% - 15px)`;
    finalPosition.transformY = '-50%';
    arrowSide = 'right';
  } else if (spaceRight >= popupWidth + 20) {
    // Position popup to the right
    finalPosition.transformX = '15px';
    finalPosition.transformY = '-50%';
    arrowSide = 'left';
  } else {
    // Fallback: best fit within grid, prefer vertical positioning
    if (spaceTop > spaceBottom) {
      finalPosition.transformY = `calc(-100% - 15px)`;
      arrowSide = 'bottom';
    } else {
      finalPosition.transformY = '15px';
      arrowSide = 'top';
    }
  }
  
  // Secondary adjustment: horizontal positioning within grid bounds
  const halfPopupWidth = popupWidth / 2;
  
  if (arrowSide === 'top' || arrowSide === 'bottom') {
    // For vertical positioning, adjust horizontal to stay within grid
    const dotRelativeToGrid = dotX - gridRect.left;
    
    if (dotRelativeToGrid - halfPopupWidth < margin) {
      // Too close to left edge of grid
      const gridRelativeLeft = margin + halfPopupWidth;
      finalPosition.x = (gridRelativeLeft / gridRect.width) * 100;
    } else if (dotRelativeToGrid + halfPopupWidth > gridRect.width - margin) {
      // Too close to right edge of grid
      const gridRelativeRight = gridRect.width - margin - halfPopupWidth;
      finalPosition.x = (gridRelativeRight / gridRect.width) * 100;
    }
  } else {
    // For horizontal positioning, adjust vertical to stay within grid
    const halfPopupHeight = popupHeight / 2;
    const dotRelativeToGrid = dotY - gridRect.top;
    
    if (dotRelativeToGrid - halfPopupHeight < margin) {
      // Too close to top of grid
      const gridRelativeTop = margin + halfPopupHeight;
      finalPosition.y = (gridRelativeTop / gridRect.height) * 100;
    } else if (dotRelativeToGrid + halfPopupHeight > gridRect.height - margin) {
      // Too close to bottom of grid
      const gridRelativeBottom = gridRect.height - margin - halfPopupHeight;
      finalPosition.y = (gridRelativeBottom / gridRect.height) * 100;
    }
  }
  
  return { ...finalPosition, arrowSide, dotX, dotY };
};
// Enhanced position popup safely function
const positionPopupSafely = (popupElement, xPercent, yPercent, gridRect, attempt = 0) => {
  if (!popupElement || !gridRect || attempt > 2) return;
  
  const positionData = applyGridAwarePositioning(popupElement, xPercent, yPercent, gridRect);
  
  // Apply the calculated position
  popupElement.style.visibility = 'visible';
  popupElement.style.left = `${positionData.x}%`;
  popupElement.style.top = `${positionData.y}%`;
  popupElement.style.transform = `translate(${positionData.transformX}, ${positionData.transformY})`;
  
  // Wait for position to be applied, then create the dynamic arrow
  setTimeout(() => {
    createDynamicArrow(popupElement, positionData.dotX, positionData.dotY, positionData.arrowSide);
    
    // Final check for any clipping within grid bounds only
    const finalRect = popupElement.getBoundingClientRect();
    
    let needsAdjustment = false;
    let adjustX = 0;
    let adjustY = 0;
    
    // Check boundaries relative to grid, not viewport
    const gridBounds = {
      left: gridRect.left + 10, // 10px margin from grid edge
      right: gridRect.right - 10,
      top: gridRect.top + 10,
      bottom: gridRect.bottom - 10
    };
    
    // Check and fix any grid boundary violations
    if (finalRect.left < gridBounds.left) {
      adjustX = gridBounds.left - finalRect.left;
      needsAdjustment = true;
    } else if (finalRect.right > gridBounds.right) {
      adjustX = gridBounds.right - finalRect.right;
      needsAdjustment = true;
    }
    
    if (finalRect.top < gridBounds.top) {
      adjustY = gridBounds.top - finalRect.top;
      needsAdjustment = true;
    } else if (finalRect.bottom > gridBounds.bottom) {
      adjustY = gridBounds.bottom - finalRect.bottom;
      needsAdjustment = true;
    }
    
    // Apply micro-adjustments if needed
    if (needsAdjustment && attempt < 2) {
      const currentLeft = parseFloat(popupElement.style.left);
      const currentTop = parseFloat(popupElement.style.top);
      
      const newXPercent = currentLeft + (adjustX / gridRect.width) * 100;
      const newYPercent = currentTop + (adjustY / gridRect.height) * 100;
      
      positionPopupSafely(popupElement, newXPercent, newYPercent, gridRect, attempt + 1);
    }
  }, 10);
};

const showMessagePopup = (message, gridX, gridY, xPercent, yPercent) => {
  setActivePopup({
    message,
    gridX,
    gridY,
    x: xPercent,
    y: yPercent
  });
  
  if (isMobileView) {
    // Mobile handling (no arrows on mobile)
    const existingOverlay = document.querySelector('.popup-overlay');
    if (existingOverlay && existingOverlay.parentNode) {
      existingOverlay.parentNode.removeChild(existingOverlay);
    }
    
    const overlay = document.createElement('div');
    overlay.className = 'popup-overlay';
    overlay.addEventListener('click', closePopup);
    document.body.appendChild(overlay);
    
    switchTab('grid');
    
    setTimeout(() => {
      const popupElement = document.querySelector('.message-popup');
      if (popupElement) {
        popupElement.style.display = 'block';
        popupElement.classList.add('visible');
      }
    }, 50);
  } else {
    // Desktop handling with grid-aware positioning
    setTimeout(() => {
      const popupElement = document.querySelector('.message-popup');
      if (popupElement && gridRef.current) {
        const gridRect = gridRef.current.getBoundingClientRect();
        popupElement.classList.add('visible');
        
        // Ensure popup is positioned relative to grid, not viewport
        positionPopupSafely(popupElement, xPercent, yPercent, gridRect);
      }
    }, 10);
  }
};

// Window resize handler that repositions arrow
const handlePopupResize = useCallback(() => {
  if (activePopup && !isMobileView) {
    const popupElement = document.querySelector('.message-popup');
    if (popupElement && gridRef.current) {
      const gridRect = gridRef.current.getBoundingClientRect();
      positionPopupSafely(popupElement, activePopup.x, activePopup.y, gridRect);
    }
  }
}, [activePopup, isMobileView]);
// Add this useEffect to handle window resize
useEffect(() => {
  window.addEventListener('resize', handlePopupResize);
  window.addEventListener('orientationchange', handlePopupResize);
  
  return () => {
    window.removeEventListener('resize', handlePopupResize);
    window.removeEventListener('orientationchange', handlePopupResize);
  };
}, [handlePopupResize]);

// Enhanced close popup function
const closePopup = useCallback(() => {
  const popupElement = document.querySelector('.message-popup');
  if (popupElement) {
    popupElement.classList.remove('visible');
    // Remove arrow classes when closing
    popupElement.classList.remove('arrow-top', 'arrow-bottom', 'arrow-left', 'arrow-right');
  }
  
  setActivePopup(null);
  
  const overlay = document.querySelector('.popup-overlay');
  if (overlay && overlay.parentNode) {
    overlay.parentNode.removeChild(overlay);
  }
}, []);

// Function to ensure popup is always accessible
const ensurePopupAccessibility = (popupElement) => {
  if (!popupElement) return;
  
  // Ensure popup is focusable
  if (!popupElement.getAttribute('tabindex')) {
    popupElement.setAttribute('tabindex', '-1');
  }
  
  // Focus the popup for screen readers
  popupElement.focus();
  
  // Add ARIA attributes
  popupElement.setAttribute('role', 'dialog');
  popupElement.setAttribute('aria-modal', 'true');
  popupElement.setAttribute('aria-label', 'Message details');
  
  // Trap focus within popup
  const focusableElements = popupElement.querySelectorAll(
    'button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  
  if (focusableElements.length > 0) {
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    popupElement.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      } else if (e.key === 'Escape') {
        closePopup();
      }
    });
  }
};

// Add this to your useEffect that handles popup display
useEffect(() => {
  if (activePopup) {
    const popupElement = document.querySelector('.message-popup');
    if (popupElement) {
      setTimeout(() => {
        ensurePopupAccessibility(popupElement);
      }, 100);
    }
  }
}, [activePopup]);


// Auto-remove expired messages
useEffect(() => {
  if (!messages.length || !isPremiumUser) return;

  const checkExpiredMessages = () => {
    const now = Math.floor(Date.now() / 1000);
    const activeMessages = messages.filter(message => message.ttl > now);

    if (activeMessages.length !== messages.length) {
      setMessages(activeMessages);

      // Close popup if the active message has expired
      if (activePopup && activePopup.message.ttl <= now) {
        closePopup();
      }
    }
  };

  // Check immediately
  checkExpiredMessages();

  // Set up interval to check every 30 seconds
  const expirationInterval = setInterval(checkExpiredMessages, 30000);

  return () => {
    clearInterval(expirationInterval);
  };
}, [messages, isPremiumUser, activePopup, closePopup]);

// Close the active popup precisely when its message expires
useEffect(() => {
  if (!activePopup) return;
  const now = Math.floor(Date.now() / 1000);
  const msUntilExpiry = Math.max(0, (activePopup.message.ttl - now) * 1000);
  const timer = setTimeout(() => closePopup(), msUntilExpiry);
  return () => clearTimeout(timer);
}, [activePopup, closePopup]);
  // Stop event propagation to prevent tab switching when interacting with popup
  const handlePopupInteraction = (e) => {
    e.stopPropagation();
  };

  const getGridPosition = (lat, lng) => {
    const x = Math.round(lng );
    const y = Math.round(lat);
    
    return { x, y };
  };

  const handleDocumentClick = useCallback((e) => {
    if (activePopup) {
      const popupElement = document.querySelector('.message-popup');
      
      if (
        popupElement && 
        !popupElement.contains(e.target) && 
        !e.target.classList.contains('message-dot') &&
        !e.target.classList.contains('message-item')
      ) {
        closePopup();
      }
    }
  }, [activePopup, closePopup]);

  useEffect(() => {
    document.addEventListener('mousedown', handleDocumentClick);
    
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [handleDocumentClick]);

  return (
    <div className="private-void-container">
      {renderMobileTabs()}
      
      <div className="private-void-layout">
        <div className={`private-void-sidebar ${isMobileView ? `mobile-tab-content ${activeTab === 'controls' ? 'active' : ''}` : ''}`}>
          <h1 className="private-void-title">Personal Void Mode</h1>
          <div className="private-void-welcome-text">Welcome to your exclusive 20×40 private void!</div>
          
          {!isLoggedIn ? (
            <div className={`private-void-controls ${isMobileView ? 'mobile-tab-content controls active' : ''}`}>
              <h3>Login to Create or View Messages</h3>
              {/* <div className="form-group">
                <label htmlFor="private-void-payment-id">Login ID:</label>
                <input 
                  type="text" 
                  id="private-void-payment-id" 
                  placeholder="Enter your Login ID"
                  value={paymentId}
                  onChange={(e) => setPaymentId(e.target.value)}
                />
              </div>

              <button 
                className="login-button" 
                onClick={handleLogin}
              >
                Login
              </button>
              <div className="login-status" style={{ color: statusColor }}>{loginStatus}</div> */}
            </div>
          ) : (
            <div className={`private-void-controls ${isMobileView ? 'mobile-tab-content controls active' : ''}`}>
              <div className="user-info">
                <div>Logged in as: <span>{  username.length > 7
          ? `${username.slice(0, 5)}${'*'.repeat(username.length - 7)}${username.slice(-2)}`
          : username}</span></div>
                <div className="token-balance">
                  <i className="token-icon">💰</i> 
                  <span>{userTokens.toLocaleString()} tokens</span>
                </div>
                {isPremiumUser && (
                  <div className="premium-badge">Premium User</div>
                )}
                <button 
                  className="logout-button" 
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
              
              {isPremiumUser ? (
                <>
                <div className="form-group">
                  <label>Enter OmegaLink:</label>
                  <div className="coordinate-field-combined">
                    <input 
                      type="text" 
                      id="jump-coordinates" 
                      placeholder=""
                      onKeyPress={(e) => e.key === 'Enter' && jumpToCoordinate()}
                    />
                    {/* <div className="coordinate-hint">Format: omega://x=X&y=Y&key=K</div> */}
                    <button 
                      className="coordinate-copy-button"
                      title="Jump to coordinates"
                      onClick={jumpToCoordinate}
                      type="button"
                    >
                      Jump
                    </button>
                  </div>
                </div>
                  <div className="form-group">
                    <label htmlFor="private-void-message-type">Message Type:</label>
                    <select 
                      id="private-void-message-type"
                      value={messageType}
                      onChange={(e) => setMessageType(e.target.value)}
                    >
                      <option value="text">Text Message</option>
                      <option value="audio">Audio Message</option>
                    </select>
                  </div>
                  
                  {messageType === 'text' ? (
                    <div className="form-group">
                      <label htmlFor="private-void-message-text">Your Message:</label>
                      <textarea 
                        id="private-void-message-text" 
                        rows="3" 
                        maxLength="1000" 
                        placeholder="Enter your message (max 1000 chars)"
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                      ></textarea>
                    </div>
                  ) : (
                    <div className="form-group">
                      <label>Record Audio (max 5 minutes):</label>
                      <div className="recording-controls">
                        {!isRecording ? (
                          <button 
                            className="record-button"
                            onClick={startRecording}
                          >
                            Start Recording
                          </button>
                        ) : (
                          <button 
                            className="record-button recording"
                            onClick={stopRecording}
                          >
                            Stop Recording ({recordingTime})
                          </button>
                        )}
                        
                        <div className="recording-status">{recordingStatus}</div>
                        
                        {audioPreviewRef.current && (
                          <div className="audio-preview">
                            <audio controls src={audioPreviewRef.current}></audio>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="form-group">
                    <label htmlFor="private-void-expiration-time">Message Expires After (minutes):</label>
                    <input 
                      type="number" 
                      id="private-void-expiration-time" 
                      min="2" 
                      max="60" 
                      value={expirationTime}
                      onChange={(e) => setExpirationTime(parseInt(e.target.value))}
                    />
                    <small>Choose between 2 and 60 minutes</small>
                  </div>
                  
                  <div className="form-group">
                    <label>Message Location:</label>
                    <div>
                      <button onClick={generateRandomCoordinates}>Use Random Location</button>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Selected Cell:</label>
                    <div className="selected-cell-display">
                      {selectedCell ? `(${selectedCell.x}, ${selectedCell.y})` : 'No cell selected'}
                    </div>
                    <small>Click on the grid to select a cell</small>
                  </div>
                  
                  {/* <div className="form-group coordinate-inputs">
                    <div className="coordinate-fields">
                      <div>
                        <label htmlFor="x-coordinate">X Coordinate:</label>
                        <input 
                          type="number" 
                          id="x-coordinate" 
                          step="0.000001" 
                          min="-5" 
                          max="5"
                          value={coordinates.x}
                          onChange={(e) => setCoordinates({...coordinates, x: e.target.value})}
                          readOnly
                        />
                      </div>
                      <div>
                        <label htmlFor="y-coordinate">Y Coordinate:</label>
                        <input 
                          type="number" 
                          id="y-coordinate" 
                          step="0.000001" 
                          min="-10" 
                          max="10"
                          value={coordinates.y}
                          onChange={(e) => setCoordinates({...coordinates, y: e.target.value})}
                          readOnly
                        />
                      </div>
                    </div>
                  </div> */}
                  
                  <div className="form-group token-info">
                    <div className="token-cost-info">
                      <div className="token-cost-header">Token Cost:</div>
                      <div className="token-cost-details">
                        <div className={`token-cost-item ${messageType === 'text' ? 'active' : ''}`}>
                          <span>Text Message:</span>
                          <span>1 token</span>
                        </div>
                        <div className={`token-cost-item ${messageType === 'audio' ? 'active' : ''}`}>
                          <span>Audio Message:</span>
                          <span>5 tokens</span>
                        </div>
                      </div>
                      <div className="token-balance-info">
                        <span>Your balance:</span>
                        <span className={userTokens < (messageType === 'audio' ? 5 : 1) ? 'insufficient' : ''}>
                          {userTokens.toLocaleString()} tokens
                        </span>
                      </div>
                    </div>
                  </div>
                  <button onClick={submitMessage} className="submit-button">Post Message</button>
                </>
              ) : (
                <div className="premium-upgrade-notice">
                  <h3>Premium Feature</h3>
                  <p>Unlock your private grid for 37,500 tokens</p>
                  <Link to="/login#" className="premium-purchase-btn">Purchase Now</Link>
                </div>
              )}
            </div>
          )}
          
        </div>
        
        <div className={`private-void-grid-main ${isMobileView ? `mobile-tab-content grid ${activeTab === 'grid' ? 'active' : ''}` : ''}`}>
          <div 
            className="private-void-grid" 
            ref={gridRef}
            onClick={handleGridClick}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {renderGridLines()}
            {renderMessageMarkers()}
            {renderCoordinateMarkings()}
            {/* {renderPersonalVoidFlag()} */}
            {renderMobileDragHint()}
            
            {!isPremiumUser && renderPremiumLock()}
            
            {selectedCell && isPremiumUser && (
              <div 
                className="selection-marker"
                style={{
                  left: `${((selectedCell.x + 10) / GRID_WIDTH) * 100}%`,
                  top: `${((20 - selectedCell.y) / GRID_HEIGHT) * 100}%`
                }}
              />
            )}
            
            {activePopup && (() => {
              const nowSec = Math.floor(Date.now() / 1000);
              if (activePopup.message.ttl - nowSec < 0) return null;
              // On mobile, positioning is fully handled by .popup-mobile-near-dot CSS (viewport-centered fixed).
              const popupStyle = isMobileView
                ? { zIndex: 9999 }
                : {
                    position: 'absolute',
                    left: `${activePopup.x}%`,
                    top: `${activePopup.y}%`,
                    transform: 'translate(-50%, -110%)',
                    marginTop: '0px',
                    zIndex: 100
                  };
              const popupEl = (
                <div
                  className={`message-popup visible ${isMobileView ? 'popup-mobile-near-dot' : ''}`}
                  style={popupStyle}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePopupInteraction(e);
                  }}
                  onTouchStart={(e) => handlePopupInteraction(e)}
                  onTouchMove={(e) => handlePopupInteraction(e)}
                  onTouchEnd={(e) => handlePopupInteraction(e)}
                >
                  <div>
                    <strong>Grid Position:</strong> ({activePopup.gridX}, {activePopup.gridY})
                  </div>

                  {(() => {
                    const now = Math.floor(Date.now() / 1000);
                    const expiresIn = activePopup.message.ttl - now;
                    return (
                      <div>
                        <strong>Expires:</strong> {
                          expiresIn > 0
                            ?
                            `in ${Math.floor(expiresIn / 60)} ${Math.floor(expiresIn / 60) === 1 ? 'min' : 'mins'}`
                            : 'in few seconds'
                        }
                      </div>
                    );
                  })()}

                  {!isLoggedIn ? (
                    <div className="login-prompt">
                      <p>Login to view message content</p>
                      <button onClick={() => {
                        closePopup();
                        document.getElementById('private-void-payment-id')?.focus();
                        if (isMobileView) switchTab('controls');
                      }}>Login</button>
                    </div>
                  ) : autoDecodingIds.has(activePopup.message.id) ? (
                    <div style={{textAlign: 'center', padding: '10px'}}>Decoding message...</div>
                  ) : null}

                  <button className="close-popup" onClick={closePopup}>
                    x
                  </button>
                </div>
              );
              return popupEl;
            })()}
          </div>
          
          {encodingKeyNotification && (
            <div className="encoding-key-notification">
              <div className="notification-content">
                <div className="notification-header">
                  <h3>{encodingKeyNotification.message}</h3>
                  <button 
                    className="close-notification"
                    onClick={() => {
                      if (notificationTimeout) {
                        clearTimeout(notificationTimeout);
                      }
                      setEncodingKeyNotification(null);
                    }}
                  >
                    ×
                  </button>
                </div>
                {/* <div className="key-display">
                  <span id="encoding-key-value">Access Code: {encodingKeyNotification.key}</span>
                  <button 
  className="copy-button"
  onClick={(e) => {
    e.stopPropagation();
    copyToClipboard(encodingKeyNotification.key, e.target);
  }}
>
  Copy
</button>
                </div>
                <div className="key-display">
                  <span id="coordinates-value">Coordinates: {encodingKeyNotification.coordinates}</span>
                  <button 
  className="copy-button"
  onClick={(e) => {
    e.stopPropagation();
    copyToClipboard(encodingKeyNotification.coordinates, e.target);
  }}
>
  Copy
</button>
                </div> */}
                <div className="key-display all">
                 
<button 
  className="copy-button copy-all"
  onClick={(e) => {
    e.stopPropagation();
    const coords = encodingKeyNotification.coordinates.split(',');
    const x = parseFloat(coords[0]).toFixed(2);
    const y = parseFloat(coords[1]).toFixed(2);
    const omegaLink = `omega://x=${x}&y=${y}&key=${encodingKeyNotification.key}`;
    copyToClipboard(omegaLink, e.target);
  }}
>
  Copy Your Omega Link
</button>
                </div>
                <div className="notification-timer">
                  This notification will close in {Math.ceil(60 - (Date.now() - encodingKeyNotification.timestamp) / 1000)} seconds
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {renderMobileActionBar()}
    </div>
  );
};

export default PrivateVoidGrid;